import { AuthenticationUser, AuthRoleEnum, useLoginValidateMutation } from '@codezo/graphql';
import { AuthLoadingScreen } from './AuthLoadingScreen';
import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { devError, devLog } from '../utils/devLog';
import { scheduleTokenRefresh } from '../utils/tokenScheduler';
import { storeUserRole } from '../utils/tokenStorage';

export interface AuthCallbackPageProps {
	/**
	 * Required role(s) for this platform
	 * Can be a single role or array of roles
	 */
	requiredRole: AuthRoleEnum | AuthRoleEnum[];
	/**
	 * Where to redirect after successful authentication
	 * @default '/dashboard'
	 */
	successRedirect?: string;
	/**
	 * A destination the user was already heading for before being sent to sign
	 * in — an emailed notification link, say. Takes precedence over
	 * `successRedirect`. Return `null` when there isn't one.
	 *
	 * Must return an in-app path: it is fed straight to `navigate`.
	 */
	resolvePendingRedirect?: () => string | null;
	/**
	 * Set user and access token in context
	 */
	setUser: (user: AuthenticationUser) => void;
	/**
	 * Set access token in context
	 */
	setAccessToken: (token: string) => void;
}

export function AuthCallbackPage({
	requiredRole,
	successRedirect = '/dashboard',
	resolvePendingRedirect,
	setUser,
	setAccessToken,
}: AuthCallbackPageProps) {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const validationInitiated = useRef(false);

	const [loginValidate] = useLoginValidateMutation({
		onError: (err) => {
			console.error('LoginValidate error:', err.message, err.graphQLErrors, err.networkError);
			devLog(err.message || 'Authentication validation failed.');
			navigate('/404');
		},
	});

	useEffect(() => {
		// Prevent double execution
		if (validationInitiated.current) {
			return;
		}

		const validateLogin = async () => {
			const token = searchParams.get('token');

			if (!token) {
				navigate('/404');
				return;
			}

			// Mark validation as initiated immediately
			validationInitiated.current = true;

			try {
				const roles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];

				const result = await loginValidate({
					variables: {
						input: {
							token,
							role: roles,
						},
					},
				});

				if (result.data?.loginValidate) {
					const { user, token } = result.data.loginValidate;

					// Check if user has required role
					if (!roles.includes(user.role)) {
						const publicAppUrl = import.meta.env.VITE_PUBLIC_APP_URL || '';
						window.location.replace(`${publicAppUrl}/auth/login?logout=true`);
						return;
					}
					// Set user and access token in context
					setUser(user as AuthenticationUser);
					setAccessToken(token);

					// Refresh token now travels as an HttpOnly cookie set by the server
					// during the loginValidate response — nothing for the frontend to store.

					// Store user role for platform routing
					storeUserRole(user.role);

					// Schedule automatic token refresh
					scheduleTokenRefresh(token);

					// Somewhere the user was already headed beats the default landing
					// page — a link that dumps them on the dashboard has failed at
					// the only job it had.
					navigate(resolvePendingRedirect?.() || successRedirect, { replace: true });
				}
			} catch (err) {
				devError('Validation error:', err);
				// Error is already handled by onError callback
			}
		};

		validateLogin();
		// eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount to process the auth callback (guarded by validationInitiated ref)
	}, []);

	return (
		<AuthLoadingScreen
			title="Authenticating..."
			description="Please wait while we validate your credentials."
		/>
	);
}
